import React, { useMemo, useEffect, useRef, useState } from "react";
import { useGetCustomerOrder } from "../../useCases/customerOrder";
import {
  isRequestLoading,
  getRequestResource,
  isRequestLoaded,
} from "../../models/RequestState";
import LoadingScreen from "../LoadingScreen";
import NotFoundScreen from "../NotFoundScreen";
import { OrderStatus, CustomerOrderResponse } from "../../generated/graphql";
import { CompanyInfo } from "../../models/company";
import OrderPaymentScreen from "../OrderPaymentScreen";
import OrderTrackingScreen from "../OrderTrackingScreen";
import { localeFromLocaleKey } from "../../i18n/types";
import { useI18n } from "../../i18n";

type ViewState = LoadingViewState | ErrorViewState | LoadedViewState;

interface LoadedViewState {
  type: "loaded";
  response: CustomerOrderResponse;
  company: CompanyInfo;
}

interface ErrorViewState {
  type: "error";
}

interface LoadingViewState {
  type: "loading";
}

interface OrderScreenProps {
  company: CompanyInfo | null | undefined;
}

const OrderScreen: React.FC<OrderScreenProps> = (props: OrderScreenProps) => {
  const { company } = props;
  const { setLocale } = useI18n();
  const orderCustomerRequest = useGetCustomerOrder();

  const [initializedLocale, setInitializedLocale] = useState(false);
  const initializedLocaleRef = useRef(initializedLocale);
  initializedLocaleRef.current = initializedLocale;

  const viewState: ViewState = useMemo(() => {
    if (isRequestLoaded(orderCustomerRequest) && company != null) {
      return {
        type: "loaded",
        response: getRequestResource(orderCustomerRequest).customerOrder,
        company,
      };
    }

    if (isRequestLoading(orderCustomerRequest)) {
      return {
        type: "loading",
      };
    }

    return {
      type: "error",
    };
  }, [orderCustomerRequest, company]);

  useEffect(() => {
    if (initializedLocaleRef.current) {
      return;
    }
    if (!isRequestLoaded(orderCustomerRequest)) {
      return;
    }
    const orderLocale = localeFromLocaleKey(
      orderCustomerRequest.resource.customerOrder.order.portalLocale
    );
    const defaultLocale = localeFromLocaleKey(
      company?.defaultCustomerPortalLocale
    );
    if (orderLocale != null) {
      setLocale(orderLocale);
    } else if (defaultLocale != null) {
      setLocale(defaultLocale);
    }
    // otherwise system default => en
    setInitializedLocale(true);
  }, [orderCustomerRequest]); //eslint-disable-line react-hooks/exhaustive-deps

  if (viewState.type === "error") {
    return <NotFoundScreen />;
  }

  if (viewState.type === "loading" || !initializedLocale) {
    return <LoadingScreen />;
  }

  if (viewState.response.order.status === OrderStatus.OrderSent) {
    return (
      <OrderPaymentScreen
        customerOrder={viewState.response}
        company={viewState.company}
      />
    );
  }

  return (
    <OrderTrackingScreen
      customerOrder={viewState.response}
      company={viewState.company}
    />
  );
};

export default OrderScreen;
